import React from 'react';
import { cn } from '../../../../utils/cn';

export interface Choice<T extends string> {
  value: T;
  label: string;
  description?: string;
}

interface ChoiceGroupProps<T extends string> {
  name: string;
  legend: string;
  /** Null when none applies, as for a preset once the ticks have been changed by hand. */
  value: T | null;
  choices: Choice<T>[];
  onChange: (value: T) => void;
}

const legendClass = 'mb-2 text-sm font-medium text-foreground';
// The radios stay visible on purpose: hiding them with sr-only inside this panel's scrollers is the
// absolute-position escape docs/claude/ui-standards.md warns about, and a visible radio says what it is.
const radioClass = 'h-4 w-4 shrink-0 border-muted-foreground/50 accent-primary';
const selectedClass = 'border-primary bg-primary/5';
const idleClass = 'border-border hover:bg-muted/60';

/** Stacked rows for choices that need a sentence to be understood. */
export function ChoiceRows<T extends string>({ name, legend, value, choices, onChange }: ChoiceGroupProps<T>) {
  return (
    <fieldset className="min-w-0">
      <legend className={legendClass}>{legend}</legend>
      <div className="space-y-2">
        {choices.map((choice) => {
          const selected = value === choice.value;
          return (
            <label
              key={choice.value}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors focus-within:ring-2 focus-within:ring-primary/20',
                selected ? selectedClass : idleClass,
              )}
            >
              <input
                type="radio"
                name={name}
                value={choice.value}
                checked={selected}
                onChange={() => onChange(choice.value)}
                className={cn(radioClass, 'mt-0.5')}
              />
              <span className="min-w-0">
                <span className={cn('block text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}>{choice.label}</span>
                {choice.description && <span className="mt-0.5 block text-xs text-muted-foreground">{choice.description}</span>}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/** A wrapping row for short labels that need no explanation. */
export function ChoicePills<T extends string>({ name, legend, value, choices, onChange }: ChoiceGroupProps<T>) {
  return (
    <fieldset className="min-w-0">
      <legend className={legendClass}>{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {choices.map((choice) => {
          const selected = value === choice.value;
          return (
            <label
              key={choice.value}
              className={cn(
                'inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors focus-within:ring-2 focus-within:ring-primary/20',
                selected ? cn(selectedClass, 'font-medium text-primary') : cn(idleClass, 'text-foreground'),
              )}
            >
              <input
                type="radio"
                name={name}
                value={choice.value}
                checked={selected}
                onChange={() => onChange(choice.value)}
                className={radioClass}
              />
              {choice.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export const SectionTitle: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <h3 id={id} className="text-base font-semibold text-foreground">{children}</h3>
);
